import React, { useState } from 'react'
import { analytics } from 'Modules/Analytics'
import { initiatives } from 'Modules/Analytics/trackActionInterface/Impact/Initiatives'
import { useAppState } from 'Context/AppContext/AppContext'
import moment, { Moment } from 'moment'
import { useMutation } from '@apollo/client'
import { Box, TextField } from '@mui/material'
import {
  Close as CloseIcon,
  Check as CheckIcon,
  EditOutlined as EditIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material'
import { colors } from '@resiliatech/birdsong'
import { CHART } from 'Apollo/Queries/Chart'
import { CHART_CATEGORY } from 'Apollo/Queries/ChartCategory'
import { UPDATE_CHART_DATA_POINTS } from 'Apollo/Mutations/UpdateChartDataPoints'
import { ChartDataPoint } from 'Generated/graphql-types'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import {
  ChartDateCell,
  ChartTableCell,
  ChartTableRow,
  RowActions,
} from './EditChartDataTable.styled'
import { DatePicker } from '../../../../SharedComponents/DatePicker/DatePicker'
import { MM_DD_YYYY } from '../utils'
import { GOALS } from '../../../../../Apollo/Queries/Goals'

type EditChartDataTableRowProps = {
  chartId: number
  popToast: (message: string) => void
  row: {
    date: string
    values: (Omit<ChartDataPoint, 'id'> & { id: number | null })[]
  }
  goalId: string
  onEditData?: (row: {
    date: string
    values: (Omit<ChartDataPoint, 'id'> & { id: number | null })[]
  }) => void
  simple?: boolean
}

export const EditChartDataTableRow = ({
  chartId,
  popToast,
  row,
  goalId,
  onEditData,
  simple,
}: EditChartDataTableRowProps) => {
  const {
    nonprofitOrgConfig: { role, nonprofitOrgRole, orgId: nonprofitOrgId },
  } = useAppState()
  const [isEditing, setIsEditing] = useState(false)
  const [editedPoints, setEditedEntries] = useState(row.values)
  const [selectedDate, setSelectedDate] = useState<Moment | null>(
    moment.utc(row.date, MM_DD_YYYY)
  )

  const [updateChartDataPoints] = useMutation(UPDATE_CHART_DATA_POINTS, {
    refetchQueries: [
      { query: CHART, variables: { goalId: parseInt(goalId, 10) } },
      { query: CHART_CATEGORY, variables: { chartId } },
      {
        query: GOALS,
        variables: { activeOnly: true, goalId, nonprofitOrgId },
      },
    ],
    awaitRefetchQueries: true,
  })

  const edit = () => {
    if (simple && onEditData) {
      onEditData(row)
    } else {
      setIsEditing(true)
    }
  }
  const handleDateChange = (date: Moment | null) => {
    setSelectedDate(date)
  }

  const handleValueChange = (newValue: string, pointId: number | null) => {
    const targetEntry = row.values.find((point) => point.id === pointId)
    if (targetEntry) {
      const newEntries = [
        ...editedPoints.filter((p) => p.id !== pointId),
        { ...targetEntry, value: parseFloat(newValue) || null },
      ]

      setEditedEntries(newEntries)
    }
  }

  const handleSaveEdits = async () => {
    analytics.trackActionCreator(initiatives.Edit_Data_Point_Submitted)({
      role,
      relationship: nonprofitOrgRole,
      goalId,
    })
    const points = editedPoints.map(({ chartCategoryId, id, value }) => ({
      chartCategoryId,
      chartId,
      date: selectedDate?.toISOString(),
      id,
      value,
    }))

    const hasValidDates = points.every((point) => point.date)

    if (!hasValidDates) {
      popToast('Please select a date before submitting.')
      return
    }

    try {
      await updateChartDataPoints({ variables: { data: points } })
      setIsEditing(false)
      popToast('Your data point was updated')
    } catch (e) {
      popToast(
        'There was an error updating your data points. Check if the date entered already exists.'
      )
    }
  }

  const handleArchive = async () => {
    const updates = row.values.map(({ chartCategoryId, id }) => ({
      chartCategoryId,
      chartId,
      id,
      shouldArchive: true,
    }))
    await updateChartDataPoints({ variables: { data: updates } })
    analytics.trackActionCreator(initiatives.Delete_Data_Point_Clicked)({
      role,
      relationship: nonprofitOrgRole,
      goalId,
    })
    popToast('Your data point was removed')
  }

  const categoryCells = row.values.map((point) => {
    const { value } = point

    return (
      <ChartTableCell key={point.id} align='left'>
        {!isEditing ? (
          <Box>{value}</Box>
        ) : (
          <TextField
            id={`editChartDataRowDataPointId${point.id}`}
            variant='outlined'
            size='small'
            defaultValue={value ?? null}
            onChange={({ target }) => {
              handleValueChange(target.value, point.id)
            }}
            required
            sx={{ width: '100%' }}
            type='number'
          />
        )}
      </ChartTableCell>
    )
  })

  return (
    <ChartTableRow key={row.date}>
      <ChartDateCell align='left'>
        {isEditing ? (
          <LocalizationProvider
            dateAdapter={AdapterMoment}
            adapterLocale='en-us'
          >
            <DatePicker
              date={selectedDate?.toISOString()}
              onDateSelected={(newDate): void => {
                handleDateChange(newDate ?? null)
              }}
              styles={{ width: '100%' }}
            />
          </LocalizationProvider>
        ) : (
          <Box>{selectedDate && selectedDate.format('MM/DD/YYYY')}</Box>
        )}
      </ChartDateCell>
      {categoryCells}
      <ChartTableCell align='left'>
        <RowActions>
          {isEditing ? (
            <CheckIcon
              cursor='pointer'
              sx={{ color: colors.interactive.primaryEnabled }}
              onClick={handleSaveEdits}
            />
          ) : (
            <EditIcon
              cursor='pointer'
              sx={{ color: colors.interactive.primaryEnabled }}
              onClick={() => edit()}
            />
          )}
          {isEditing ? (
            <CloseIcon
              cursor='pointer'
              sx={{ color: colors.interactive.primaryEnabled }}
              onClick={() => setIsEditing(false)}
            />
          ) : (
            <DeleteIcon
              cursor='pointer'
              sx={{ color: colors.interactive.primaryEnabled }}
              onClick={handleArchive}
            />
          )}
        </RowActions>
      </ChartTableCell>
    </ChartTableRow>
  )
}
